// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { useState } from "react";
import type { BlockConfigOf } from "@/lib/blocks/schemas";
import { LIGHT_BG_CLASSES as BG_CLASSES } from "./backgrounds";
import { SectionLabel } from "./SectionLabel";

type FaqConfig = BlockConfigOf<"faq">;


/**
 * Client renderer for the FAQ block. Two layouts (#101):
 * - `accordion` (default): one answer open at a time; click an open item
 *   to close it. Markup unchanged from before #101 apart from the
 *   optional section label.
 * - `grid`: two columns on desktop, one on mobile, every answer always
 *   visible under a black top rule. No state, no toggles.
 */
export function FaqBlock({ config }: { config: FaqConfig }) {
  return config.layout === "grid" ? (
    <FaqGrid config={config} />
  ) : (
    <FaqAccordion config={config} />
  );
}

function FaqGrid({ config }: { config: FaqConfig }) {
  return (
    <section
      className={`w-full py-20 px-6 md:px-12 lg:px-24 ${BG_CLASSES[config.background]}`}
    >
      <div className="max-w-6xl mx-auto">
        <SectionLabel text={config.sectionLabel} />
        {config.heading && (
          <h2 className="font-[family-name:var(--font-display)] text-4xl md:text-6xl mb-10">
            {config.heading}
          </h2>
        )}
        {config.items.length === 0 && (
          <p className="text-sm text-stone-500 italic">No FAQ items yet.</p>
        )}
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {config.items.map((item, i) => (
            <div key={i} className="w-full border-t-2 border-black pt-6">
              <dt className="font-[family-name:var(--font-display)] text-2xl md:text-3xl leading-tight mb-4">
                {item.question}
              </dt>
              <dd
                className="text-opichi-muted leading-relaxed"
                dangerouslySetInnerHTML={{ __html: item.answer }}
              />
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function FaqAccordion({ config }: { config: FaqConfig }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      className={`w-full py-20 px-6 md:px-12 lg:px-24 ${BG_CLASSES[config.background]}`}
    >
      <div className="max-w-3xl mx-auto">
        <SectionLabel text={config.sectionLabel} />
        {config.heading && (
          <h2 className="font-[family-name:var(--font-display)] text-4xl md:text-6xl mb-10">
            {config.heading}
          </h2>
        )}
        {config.items.length === 0 && (
          <p className="text-sm text-stone-500 italic">No FAQ items yet.</p>
        )}
        <ul className="space-y-3">
          {config.items.map((item, i) => {
            const open = openIndex === i;
            return (
              <li key={i} className="brutal-border-sm bg-white">
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 cursor-pointer"
                  aria-expanded={open}
                >
                  <span className="font-[family-name:var(--font-display)] text-xl md:text-2xl">
                    {item.question}
                  </span>
                  <span className="text-2xl text-stone-500 shrink-0">
                    {open ? "–" : "+"}
                  </span>
                </button>
                {open && (
                  <div
                    className="px-5 pb-5 prose-brutal text-stone-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
